import React, { useContext, useState, useEffect } from 'react';
import SinglePageForm, {
    getEmailRules,
    getPasswordRules,
} from '../common/SinglePageForm';
import { Input, Button, Alert, Form, Space } from 'antd';
import { NavLink, useLocation } from 'react-router-dom';
import { login } from '../services/user';
import { UnauthorizedApiError } from '../services/error';
import { useQueryParam, StringParam } from 'use-query-params';
import { APP_CCX, CustomAppContext } from '../common/CustomAppContext';
import './LoginPage.less';
import { trackCustomEvent } from '../helpers';

interface LoginFormValues {
    login: string;
    password: string;
}

export default LoginPage;

/**
 * @name Login
 * @desc Login component.
 */
function LoginPage(): JSX.Element {
    const [form] = Form.useForm<LoginFormValues>();
    const location = useLocation();

    useEffect(() => {
        if (window?.gtag) {
            window.gtag('event', 'page_view', {
                page_path: location.pathname + location.search + location.hash,
                page_search: location.search,
                page_hash: location.hash,
            });
        }
    }, [location]);

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const {
        currentApp,
        getCustomAppPath,
        getCustomAppButtonStyle,
        getCustomAppRedirectUrl,
    } = useContext(CustomAppContext);
    const [redirectTo] = useQueryParam('redirectTo', StringParam);

    /**
     * @name handleSubmit
     * @desc Handles form submit.
     * @private
     */
    const handleSubmit = async (values: LoginFormValues): Promise<void> => {
        setLoading(true);
        try {
            await login(values);
            window.location.assign(redirectTo || getCustomAppRedirectUrl());
            trackCustomEvent(currentApp, 'user_login_success', 'click');
        } catch (err: any) {
            if (err instanceof UnauthorizedApiError) {
                setError('Incorrect username or password');
            } else {
                setError(err.message);
            }
            trackCustomEvent(currentApp, 'user_login_failed', 'click');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitFailed = (errorInfo: any): void => {
        console.log('Failed:', errorInfo);
        trackCustomEvent(currentApp, 'user_login_error', 'click');
        setError('Please check your input and try again.');
    };

    return (
        <SinglePageForm
            form={form}
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailed}
            data-testid="login-form"
        >
            <SinglePageForm.Content>
                <Space direction="vertical" size={15} style={{ width: '100%' }}>
                    <h1 className="LoginPage-form-title">Log in</h1>

                    {error ? (
                        <Alert
                            message={
                                <span className="LoginPage-error-message">
                                    {error}
                                </span>
                            }
                            description={
                                <span className="LoginPage-error-description">
                                    Please try again.
                                </span>
                            }
                            type="error"
                            showIcon
                        />
                    ) : null}
                    <Form.Item
                        label="Email"
                        name="login"
                        rules={getEmailRules()}
                        validateTrigger="onBlur"
                    >
                        <Input
                            data-testid="login-form-input-email"
                            disabled={loading}
                            placeholder="Enter your email"
                        />
                    </Form.Item>
                    <Form.Item
                        label="Password"
                        name="password"
                        rules={getPasswordRules()}
                        validateTrigger="onBlur"
                    >
                        <Input.Password
                            data-testid="login-form-input-password"
                            disabled={loading}
                            placeholder="Enter your password"
                        />
                    </Form.Item>
                </Space>
                <SinglePageForm.Footer>
                    <>
                        <NavLink
                            to={getCustomAppPath('/forgot-password')}
                            className={`LoginPage-forgot-password-${currentApp}`}
                        >
                            Forgot your password?
                        </NavLink>
                    </>
                    <br />
                    <br />
                    <Button
                        data-testid="login-form-button-submit"
                        loading={loading}
                        type="primary"
                        htmlType="submit"
                        className="LoginPage-form-button"
                        style={getCustomAppButtonStyle()}
                    >
                        Log in
                    </Button>
                </SinglePageForm.Footer>
            </SinglePageForm.Content>
            <div className={`LoginPage-form-footer-${currentApp}`}>
                <Form.Item>
                    Don't have an account?{' '}
                    <NavLink to={getCustomAppPath('/register')}>
                        Sign up
                    </NavLink>
                </Form.Item>
            </div>
        </SinglePageForm>
    );
}
